<template>
  <a-spin :spinning="loading">
    <div
      :class="[bem.b(), props.circled ? bem.is('circled') : '']"
      :style="getWrapperStyle"
    >
      <img
        v-show="isReady"
        ref="imgElRef"
        :src="src"
        :alt="alt"
        :crossorigin="crossorigin"
        :style="getImageStyle"
        @load="handleLoading"
      />
    </div>
  </a-spin>
</template>
<script lang="ts" setup>
import {
  onMounted,
  ref,
  computed,
  onUnmounted,
  watch,
  type CSSProperties,
} from 'vue'
import { createNamespace } from '@ll-plus/utils'
//@ts-ignore
import Cropper from 'cropperjs'
// import 'cropperjs/dist/cropper.css'
import { useDebounceFn } from '@vueuse/shared'
import {
  cropperImageProps,
  cropperImageEmits,
  defaultCropperOptions,
  type CropendResult,
} from './cropper-image'

defineOptions({ name: 'CropperImage' })
const props = defineProps(cropperImageProps)
const emits = defineEmits(cropperImageEmits)

const bem = createNamespace('cropper-image')

const imgElRef = ref<HTMLImageElement>()
const cropper = ref<Cropper>()
const isReady = ref(false)
const loading = ref(props.loading)
const getImageStyle = computed(
  (): CSSProperties => ({
    height: props.height,
    maxWidth: '100%',
    ...props.imageStyle,
  })
)
const getWrapperStyle = computed(
  (): CSSProperties => ({ height: `${props.height}`.replace(/px/, '') + 'px' })
)

const init = async () => {
  const imgEl = imgElRef.value
  if (!imgEl) {
    return
  }
  loading.value = true
  cropper.value = new Cropper(imgEl, {
    ...defaultCropperOptions,
    ready() {
      isReady.value = true
      loading.value = false
      realTimeCroppered()
      //@ts-ignore
      emits('onReady', cropper.value)
    },
    crop() {
      debounceRealTimeCroppered()
    },
    zoom() {
      debounceRealTimeCroppered()
    },
    cropmove() {
      debounceRealTimeCroppered()
    },
    ...props.options,
  })
}

// Real-time display preview
const realTimeCroppered = () => {
  props.realTimePreview && croppered()
}

const debounceRealTimeCroppered = useDebounceFn(realTimeCroppered, 80)

// event: return base64 and width and height information after cropping
const croppered = () => {
  if (!cropper.value) {
    return
  }
  const imgInfo = cropper.value.getData()
  const canvas = props.circled
    ? getRoundedCanvas()
    : cropper.value.getCroppedCanvas()
  canvas.toBlob((blob) => {
    if (!blob) {
      return
    }
    const fileReader: FileReader = new FileReader()
    fileReader.readAsDataURL(blob)
    fileReader.onloadend = (e) => {
      emits('cropend', {
        imgBase64: e.target?.result ?? '',
        imgInfo,
      } as CropendResult)
    }
    fileReader.onerror = () => {
      emits('cropendError')
    }
  }, 'image/png')
}

// Get a circular picture canvas
const getRoundedCanvas = () => {
  const sourceCanvas = cropper.value!.getCroppedCanvas()
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')!
  const width = sourceCanvas.width
  const height = sourceCanvas.height
  canvas.width = width
  canvas.height = height
  context.imageSmoothingEnabled = true
  context.drawImage(sourceCanvas, 0, 0, width, height)
  context.globalCompositeOperation = 'destination-in'
  context.beginPath()
  context.arc(
    width / 2,
    height / 2,
    Math.min(width, height) / 2,
    0,
    2 * Math.PI,
    true
  )
  context.fill()
  return canvas
}
//获取图片加载中
const handleLoading = () => {
  loading.value = true
}

onMounted(init)

onUnmounted(() => {
  cropper.value?.destroy()
})

watch(
  () => props.loading,
  (val) => {
    loading.value = val
  }
)
</script>
